import React, { useState } from 'react';
import { X } from 'lucide-react';
import { SmartCalendar } from '@/apps/master-plan/shared/components/SmartCalendar';
import { toLogicDate } from '@/utils/date-utils';

interface TemporalEditorProps {
    startDate: string;
    startTime: string;
    endDate: string;
    endTime: string;
    timeIntervals: string[];
    formatDisplayDate: (date: string) => string;
    onUpdate: (updates: any) => void;
}

/**
 * TemporalEditor: Atomic component for Date and Time range selection.
 * Handles both Start and End temporal bounds.
 * SHADOW VERSION (.tsx)
 */
export const TemporalEditor: React.FC<TemporalEditorProps> = ({
    startDate,
    startTime,
    endDate,
    endTime,
    timeIntervals,
    formatDisplayDate,
    onUpdate
}) => {
    const [openDatePicker, setOpenDatePicker] = useState<'START' | 'END' | null>(null);
    const [currentMonth, setCurrentMonth] = useState<Date>(new Date());

    const parseDate = (dateStr: string) => {
        if (!dateStr) return new Date();
        const [y, m, d] = dateStr.split('-').map(Number);
        return new Date(y, m - 1, d);
    };

    const handleDateSelect = (date: Date) => {
        const dateStr = toLogicDate(date);
        if (openDatePicker === 'START') onUpdate({ startDate: dateStr });
        else onUpdate({ endDate: dateStr });
        setOpenDatePicker(null);
    };

    const selectStyle: React.CSSProperties = { 
        all: 'unset', 
        backgroundColor: '#111', 
        border: '1px solid #222', 
        borderRadius: '8px', 
        padding: '10px', 
        color: '#fff', 
        fontSize: '14px', 
        width: '100%', 
        boxSizing: 'border-box' 
    };

    return (
        <div style={{ borderBottom: '1px solid #222', padding: '12px 15px', display: 'flex', alignItems: 'center', gap: '10px' }}>
            <div style={{ flex: 1, display: 'flex', gap: '4px' }}>
                <button 
                    onClick={() => setOpenDatePicker('START')} 
                    style={{ ...selectStyle, flex: 1.2, padding: '8px 5px', fontSize: '12px', textAlign: 'center', cursor: 'pointer' }}
                >
                    {formatDisplayDate(startDate)}
                </button>
                <select 
                    value={startTime} 
                    onChange={(e) => onUpdate({ startTime: e.target.value })} 
                    style={{ ...selectStyle, flex: 1, textAlign: 'center', padding: '8px 2px', fontFamily: 'monospace', fontSize: '13px' }}
                >
                    {timeIntervals.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
            </div>

            <span style={{ color: '#333', fontWeight: '900' }}>—</span>

            <div style={{ flex: 1, display: 'flex', gap: '4px' }}>
                <select 
                    value={endTime} 
                    onChange={(e) => onUpdate({ endTime: e.target.value })} 
                    style={{ ...selectStyle, flex: 1, textAlign: 'center', padding: '8px 2px', fontFamily: 'monospace', fontSize: '13px' }}
                >
                    {timeIntervals.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
                <button 
                    onClick={() => setOpenDatePicker('END')} 
                    style={{ ...selectStyle, flex: 1.2, padding: '8px 5px', fontSize: '12px', textAlign: 'center', cursor: 'pointer' }}
                >
                    {formatDisplayDate(endDate)}
                </button>
            </div>

            {/* DATE PICKER MODAL */}
            {openDatePicker && (
                <div style={{ position: 'fixed', inset: 0, zIndex: 10001, backgroundColor: 'rgba(0,0,0,0.9)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
                    <div style={{ backgroundColor: '#0a0a0a', border: '1px solid #222', borderRadius: '16px', padding: '20px', width: '100%', maxWidth: '360px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '15px' }}>
                            <span style={{ fontWeight: '900', color: '#f29b11', textTransform: 'uppercase', fontSize: '12px' }}>Select {openDatePicker} Date</span>
                            <button onClick={() => setOpenDatePicker(null)} style={{ all: 'unset', cursor: 'pointer' }}>
                                <X size={20} color="#666" />
                            </button>
                        </div>
                        <SmartCalendar 
                            currentMonth={currentMonth} 
                            onMonthChange={setCurrentMonth} 
                            selectedDate={parseDate(openDatePicker === 'START' ? startDate : endDate)} 
                            onSelectDate={handleDateSelect} 
                        />
                    </div>
                </div>
            )}
        </div>
    );
};
